/**
 * Error helpers shared by every mutation in the onsite app.
 *
 * supabase-js never throws on a failed request; it returns `{ error }`. A
 * transport failure (no network, DNS, CORS) surfaces as an error whose
 * message is the underlying fetch exception and whose code is empty. Those
 * are worth queueing in the offline outbox; a Postgres / RLS / validation
 * error is permanent and must be shown to the user instead.
 */

export interface ErrorLike {
  message?: string | null;
  code?: string | null;
  details?: string | null;
  hint?: string | null;
}

export function errorMessage(err: unknown, fallback = 'Unknown error'): string {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message || fallback;
  if (typeof err === 'object') {
    const e = err as ErrorLike;
    if (typeof e.message === 'string' && e.message) return e.message;
  }
  return fallback;
}

/** True when the request never reached PostgREST (offline, DNS, CORS, aborted). */
export function isTransportError(err: unknown): boolean {
  if (!err) return false;
  const message = errorMessage(err, '');
  const code = typeof err === 'object' && err !== null ? (err as ErrorLike).code : undefined;
  if (code) return false;
  return /failed to fetch|networkerror|network request failed|load failed|fetch failed|aborted|timed? ?out/i.test(
    message,
  );
}

/** Translate the most common Postgres / PostgREST codes into plain language. */
export function friendlyDbError(err: unknown): string {
  const message = errorMessage(err);
  const code = typeof err === 'object' && err !== null ? (err as ErrorLike).code : undefined;
  switch (code) {
    case '42501':
      return 'You do not have permission to make this change.';
    case '23505':
      return 'A record with the same key already exists.';
    case '23503':
      return 'This record references something that no longer exists.';
    case '23502':
      return 'A required field is missing.';
    case 'PGRST301':
      return 'Your session has expired. Sign in again.';
    default:
      return message;
  }
}

export function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}
